// @flow

// inspiration - https://codepen.io/xgad/post/svg-radial-progress-meters

import * as React from "react";
import cx from "classnames";
import styled, { css as styledCss, keyframes } from "styled-components";
import glamorous from "glamorous";
import { css } from "glamor";
import isString from "voca";
import { isNumber } from "lodash";

type Size = "xxs" | "xs" | "sm" | "md" | "lg" | "xl" | "xl2" | number;
type Props = {
	colorName: string,
	size?: Size,
	percentage: number,
	className?: string,
	overlay: boolean
};

type State = {
	prevPercentage: number,
	currPercentage: number,
}

const calcPercentage = (circumference: number, percentage: number) => circumference * (1 - (percentage / 100));

const GIProgressText = glamorous.div(({ colorName }: { colorName: number }) => cx(
	"absolute top-0 left-0",
	"w-100 h-100",
	"flex items-center justify-center",
	"fw5",
	[`c-${colorName}`],
), ({ fontSize }: { fontSize: number }) => ({ fontSize }));

const GIProgressSvg = glamorous.svg(({ colorName }: { colorName: string }) => cx(
	"ProgressWithPercentage",
	"db",
	[`circle-${colorName}`],
),
{
	transform: "rotate(-90deg)",
});

const GIProgressCircle = glamorous.circle(({
											   progresssAnimation, endPoint, strokeWidth, circumference,
										   }) => ({
	animation: `${progresssAnimation} 200ms linear`,
	strokeDashoffset: endPoint,
	strokeWidth,
	strokeDasharray: circumference,
}));

const ProgressWithPercentage = ({
	size = 0, start = 0, end = 0, colorName, className,
}: { size: number, start: number, end: number, colorName: string, className?: string }) => {
	const strokeWidth = Math.ceil(size / 8.3);
	const fontSize = Math.ceil(size / 4);
	const radius = (size / 2) - (strokeWidth / 2);
	const circumference = 2 * Math.PI * radius;
	const startPoint = calcPercentage(circumference, start);
	const endPoint = calcPercentage(circumference, end);
	const progressAnimation = css.keyframes({
		from: { strokeDashoffset: startPoint },
		to: { strokeDashoffset: endPoint },
	});

	return <glamorous.Div className={cx("relative", className)} width={size} height={size}>
		<GIProgressSvg colorName={colorName} width={size} height={size}
					   viewBox={`0 0 ${size} ${size}`}>
			<GIProgressCircle cx={size / 2} cy={size / 2} r={radius} strokeWidth={strokeWidth}
							  endPoint={endPoint} keyFrames={progressAnimation}
							  circumference={circumference} fill="none"/>
		</GIProgressSvg>
		{fontSize > 6
			? <GIProgressText colorName={colorName}
							  fontSize={fontSize}>{end}%</GIProgressText> : null}
	</glamorous.Div>;
};

const overlayStyle = `
        position:  absolute;
        z-index: 1;
        left: 50%;
        top: 50%;
        transform: translate(-50%, -50%);
`;

const rotateAnimation = keyframes`
    from {
        transform: rotate(0)
    }
    to {
        transform: rotate(360deg)
    }
`;

const StyledInfiniteProgress = styled.div`
    ${({ size }: { size: ?Size }) => (isNumber(size)
		? styledCss`
        width: ${size}px;
        height: ${size}px;
    ` : !size
			? styledCss`
                width: 100%;
                height: 100%;
            ` : null)}

    ${({ overlay }: { overlay: boolean }) => (overlay ? styledCss`${overlayStyle}` : "")};

    animation: ${rotateAnimation} 1s infinite linear;
`;

const InfiniteProgress = ({
							  size, colorName, className, overlay = false,
						  }: { size?: Size, colorName: string, className?: string, overlay: boolean }) =>
	<StyledInfiniteProgress
		className={`InfiniteProgress db circle-${colorName} ${isString(size) && size ? `box-${size}` : ""} ${className || ""}`}
		colorName={colorName} size={size} overlay={overlay}>
		<svg viewBox="0 0 100 100" preserveAspectRatio="xMidYMid">
			<circle cx="50%" cy="50%" fill="none" r="42"
				strokeDasharray="197.92033717615698 67.97344572538566" strokeWidth="13"/>
		</svg>
	</StyledInfiniteProgress>;

class Progress extends React.Component<Props, State> {
	static defaultProps = {
		colorName: "in-progress",
		percentage: -1,
		overlay: false,
		size: 0,
	};

	static getDerivedStateFromProps(nextProps: Props, prevState: State) {
		if (nextProps.percentage !== prevState.currPercentage) {
			return {
				prevPercentage: prevState.currPercentage,
				currPercentage: nextProps.percentage,
			};
		}

		return null;
	}

	state = {
		prevPercentage: 0,
		currPercentage: 0,
	};

	render() {
		const showPercentage = this.props.percentage >= 0 && isNumber(this.props.size);
		const size: any = isNumber(this.props.size) ? this.props.size : 0;

		return <>
			{showPercentage
				? <ProgressWithPercentage className={this.props.className}
										  colorName={this.props.colorName} size={size}
										  start={this.state.prevPercentage}
										  end={this.state.currPercentage}/> : null}

			{!showPercentage
				? <InfiniteProgress colorName={this.props.colorName} size={this.props.size}
					className={this.props.className}
					overlay={this.props.overlay}/> : null}
		</>;
	}
}

export default Progress;
